import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { fmtDate, fmtNum, fmtUsd } from "../utils/fmt";
import { useToast } from "../utils/useToast";
import SlotNumber from "../components/SlotNumber";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGear, faXmark, faShareNodes } from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";

export default function AdminCalculator({ authed, role }) {
  const { toast, showToast } = useToast();

  const [msg, setMsg] = useState("");

  // settings
  const [settings, setSettings] = useState(null);

  // inputs
  const [customerName, setCustomerName] = useState("");
  const [weight, setWeight] = useState("");
  const [weightUnit, setWeightUnit] = useState("g");

  const [ptPpm, setPtPpm] = useState(""); // instead of 0
  const [pdPpm, setPdPpm] = useState("");
  const [rhPpm, setRhPpm] = useState("");

  const [lmeUnit, setLmeUnit] = useState("per_oz");
  const [ptLme, setPtLme] = useState(""); // instead of 0
  const [pdLme, setPdLme] = useState("");
  const [rhLme, setRhLme] = useState("");
  const refDoneBtn = useRef(null);
  const [fxRate, setFxRate] = useState("");

  const [result, setResult] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // entry or null
  const [deletingId, setDeletingId] = useState(null);
  const refJumpToggle = useRef(null);
  const refPtJump = useRef(null);
  const refPdJump = useRef(null);
  const refRhJump = useRef(null);
  // history
  const [entries, setEntries] = useState([]);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("desc"); // desc|asc
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [lastEntryId, setLastEntryId] = useState(null);
  const [showCalcUnitModal, setShowCalcUnitModal] = useState(false);
  const [lmeOpen, setLmeOpen] = useState(false);
  const [weightOpen, setWeightOpen] = useState(false);
  const [ptJumpPct, setPtJumpPct] = useState("");
  const [pdJumpPct, setPdJumpPct] = useState("");
  const [rhJumpPct, setRhJumpPct] = useState("");
  const refCustomer = useRef(null);

  const refPtLme = useRef(null);
  const refPdLme = useRef(null);
  const refRhLme = useRef(null);

  const refWeight = useRef(null);

  const refPtPpm = useRef(null);
  const refPdPpm = useRef(null);
  const refRhPpm = useRef(null);

  const [cap, setCap] = useState(10);
  const [pageSize] = useState(20);
  const [nextCursor, setNextCursor] = useState(null);
  const refFx = useRef(null);
  const refCalcBtn = useRef(null);
  const refLmeTrigger = useRef(null);
  const refWeightTrigger = useRef(null);

  const refLmeOptOz = useRef(null);
  const refWeightOptG = useRef(null);

  const [showPpmJump, setShowPpmJump] = useState(false); // default hidden
  const [jumpDetach, setJumpDetach] = useState({ pt: false, pd: false, rh: false });
  const [ppmDetach, setPpmDetach] = useState({ pt: false, pd: false, rh: false });
  const LME_UNIT_LABEL = {
  per_oz: "per Toz",
  per_g: "per g",
  };

  const WEIGHT_UNIT_LABEL = {
    g: "g",
    kg: "kg",
  };

  const savedJump = settings?.ppmJumpPct || {};
  const savedPtJump = savedJump.pt ?? 0;
  const savedPdJump = savedJump.pd ?? 0;
  const savedRhJump = savedJump.rh ?? 0;

  const LAST_RATES_KEY = useMemo(() => {
  // unique per user+role; if you have userId, use it instead of role
  return `adminCalc:lastRates:${role || "anon"}`;
}, [role]);

function safeParse(json) {
  try { return JSON.parse(json); } catch { return null; }
}

function persistLastRates(payload) {
  // payload: { lmeUnit, lme:{pt,pd,rh}, fxRate }
  try {
    localStorage.setItem(LAST_RATES_KEY, JSON.stringify(payload));
  } catch {}
}

function readLastRates() {
  try {
    return safeParse(localStorage.getItem(LAST_RATES_KEY) || "");
  } catch {
    return null;
  }
}

function onSelectTriggerEnter(e, isOpen, setOpen, firstOptionRef) {
  if (e.key !== "Enter" && e.key !== "NumpadEnter") return;

  // Stop native button "click on Enter"
  e.preventDefault();
  e.stopPropagation();

  // If closed: open then focus first option
  if (!isOpen) {
    setOpen(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => focusNext(firstOptionRef));
    });
    return;
  }

  // If already open: just go into the list (first option)
  focusNext(firstOptionRef);
}

function onToggleEnter(e, isOpen, openFn, firstInputRef) {
  if (e.key !== "Enter" && e.key !== "NumpadEnter") return;

  // stop native button click on Enter
  e.preventDefault();
  e.stopPropagation();

  if (!isOpen) {
    openFn(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => focusNext(firstInputRef));
    });
    return;
  }

  focusNext(firstInputRef);
}

function focusNext(ref) {
  if (!ref?.current) return;
  ref.current.focus({ preventScroll: true });
  ref.current.select?.();
}

function onAdvanceKeyDown(e, nextRef) {
  if (e.key === "Enter" || e.key === "NumpadEnter") {
    e.preventDefault();
    focusNext(nextRef);
  }
}

function openSectionAndFocus(setOpen, inputRef) {
  setOpen(true);
  // wait until the DOM renders the opened section
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      focusNext(inputRef);
    });
  });
}

function onEnterOpenPpmJump(e) {
  if (e.key !== "Enter" && e.key !== "NumpadEnter") return;
  e.preventDefault();
  openSectionAndFocus(setShowPpmJump, refPtJump);
}

function onEnterRun(e) {
  if (e.key === "Enter" || e.key === "NumpadEnter") {
    e.preventDefault();
    run();
  }
}

  function jumpDisplay(metal) {
    const det = jumpDetach[metal];

    if (metal === "pt") return det ? ptJumpPct : String(savedPtJump ?? 0);
    if (metal === "pd") return det ? pdJumpPct : String(savedPdJump ?? 0);
    return det ? rhJumpPct : String(savedRhJump ?? 0);
  }

  function detachJump(metal) {
  setJumpDetach((s) => {
    if (s[metal]) return s;
    // when first detaching, prefill typed state from saved so editing is easy
    if (metal === "pt") setPtJumpPct(String(savedPtJump ?? 0));
    if (metal === "pd") setPdJumpPct(String(savedPdJump ?? 0));
    if (metal === "rh") setRhJumpPct(String(savedRhJump ?? 0));
    return { ...s, [metal]: true };
  });
}

function reattachJump(metal) {
  setJumpDetach((s) => ({ ...s, [metal]: false }));
  if (metal === "pt") setPtJumpPct("");
  if (metal === "pd") setPdJumpPct("");
  if (metal === "rh") setRhJumpPct("");
}

function num0(v) {
  if (v === "" || v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

// effective jump % = override if detached, else saved settings
function effectiveJumpPct(metal) {
  const s = settings?.ppmJumpPct || {};

  if (metal === "pt") return jumpDetach.pt ? num0(ptJumpPct) : num0(s.pt);
  if (metal === "pd") return jumpDetach.pd ? num0(pdJumpPct) : num0(s.pd);
  return jumpDetach.rh ? num0(rhJumpPct) : num0(s.rh);
}

function effectivePpmValue(enteredPpm, jumpPct) {
  const e = num0(enteredPpm);
  const j = num0(jumpPct);
  return e * (1 + j / 100);
}

const ptJumpEff = effectiveJumpPct("pt");
const pdJumpEff = effectiveJumpPct("pd");
const rhJumpEff = effectiveJumpPct("rh");

const ptPpmEff = effectivePpmValue(ptPpm, ptJumpEff);
const pdPpmEff = effectivePpmValue(pdPpm, pdJumpEff);
const rhPpmEff = effectivePpmValue(rhPpm, rhJumpEff);

function detachPpm(metal) {
  setPpmDetach((s) => ({ ...s, [metal]: true }));
}
function reattachPpm(metal) {
  setPpmDetach((s) => ({ ...s, [metal]: false }));
}

// what we show inside the PPM input
function ppmDisplay(metal) {
  if (metal === "pt") return ppmDetach.pt ? ptPpm : String(Math.round(ptPpmEff));
  if (metal === "pd") return ppmDetach.pd ? pdPpm : String(Math.round(pdPpmEff));
  return ppmDetach.rh ? rhPpm : String(Math.round(rhPpmEff));
}

useEffect(() => {
  if (!authed || role !== "admin") return;
  setNextCursor(null);
  loadEntries(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [authed, role, search, sort, cap]);

useEffect(() => {
  if (!authed || role !== "admin") return;

  const saved = readLastRates();
  if (!saved) return;

  // only apply if user hasn't already set values in this session
  const noLmeTyped = ptLme === "" && pdLme === "" && rhLme === "";
  const noFxTyped = fxRate === "";

  if (noLmeTyped && saved.lme) {
    setLmeUnit(saved.lmeUnit || "per_oz");
    setPtLme(saved.lme.pt ?? "");
    setPdLme(saved.lme.pd ?? "");
    setRhLme(saved.lme.rh ?? "");
  }

  if (noFxTyped) {
    setFxRate(saved.fxRate == null ? "" : String(saved.fxRate));
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [authed, role]);


useEffect(() => {
  if (!authed || role !== "admin") return;

  let cancelled = false;

  api.adminCalcGetSettings()
    .then((d) => {
      if (cancelled) return;
      setSettings(d.settings || {});
    })
    .catch((e) => {
      if (cancelled) return;
      // intentionally don't include showToast in deps; this is a one-time fetch
      showToast(e.message || "Failed to load settings", "err");
    });

  return () => {
    cancelled = true;
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [authed, role]);

useEffect(() => {
  if (!showCalcUnitModal) setWeightOpen(false);
}, [showCalcUnitModal]);

async function deleteEntry(entry) {
  if (!entry?._id) return;

  setDeletingId(entry._id);
  try {
    await api.adminCalcDeleteEntry(entry._id);
    showToast("Entry deleted", "ok");
    setConfirmDelete(null);
    await loadEntries();
  } catch (e) {
    showToast(e.message || "Failed to delete entry", "err");
  } finally {
    setDeletingId(null);
  }
}

async function loadEntries(reset = true) {
  if (!authed || role !== "admin") return;
  setLoadingEntries(true);
  try {
    const d = await api.adminCalcEntries({
      search,
      sort,
      limit: pageSize,
      cap,
      cursor: reset ? "" : nextCursor || "",
    });

    const newRows = d.entries || [];
    setEntries((prev) => (reset ? newRows : [...prev, ...newRows]));
    setNextCursor(d.nextCursor || null);
  } catch (e) {
    showToast(e.message || "Failed to load entries", "err");
  } finally {
    setLoadingEntries(false);
  }
}


async function saveSettings() {
  setMsg("");
  try {
    const toNum = (x, fallback = 0) => {
      if (x === "" || x == null) return fallback;
      const n = Number(x);
      return Number.isFinite(n) ? n : fallback;
    };

    const payload = {
      moisturePct: toNum(settings?.moisturePct, 0),

      leaseDays: {
        pt: toNum(settings?.leaseDays?.pt, 0),
        pd: toNum(settings?.leaseDays?.pd, 0),
        rh: toNum(settings?.leaseDays?.rh, 0),
      },

      chargeUsdPerG: {
        pt: toNum(settings?.chargeUsdPerG?.pt, 0),
        pd: toNum(settings?.chargeUsdPerG?.pd, 0),
        rh: toNum(settings?.chargeUsdPerG?.rh, 0),
      },

      recoveryRate: {
        pt: toNum(settings?.recoveryRate?.pt, 0.98),
        pd: toNum(settings?.recoveryRate?.pd, 0.98),
        rh: toNum(settings?.recoveryRate?.rh, 0.91),
      },

      ppmJumpPct: {
        pt: toNum(settings?.ppmJumpPct?.pt, 0),
        pd: toNum(settings?.ppmJumpPct?.pd, 0),
        rh: toNum(settings?.ppmJumpPct?.rh, 0),
      },
      marginPct: toNum(settings?.marginPct, 0),
    };

    const d = await api.adminCalcPutSettings(payload);

    // after save, you can store numeric values back (or keep strings; either is fine)
    setSettings(d.settings || payload);
    showToast("Settings saved", "ok");
  } catch (e) {
    setMsg(e.message);
  }
}

  async function run() {
    setMsg("");
    setResult(null);

    const num0 = (v) => {
      if (v === "" || v == null) return 0;
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
      };

      const jump = {};
        if (jumpDetach.pt) jump.pt = ptJumpPct === "" ? 0 : Number(ptJumpPct);
        if (jumpDetach.pd) jump.pd = pdJumpPct === "" ? 0 : Number(pdJumpPct);
        if (jumpDetach.rh) jump.rh = rhJumpPct === "" ? 0 : Number(rhJumpPct);

    try {
      const input = {
        weight: num0(weight),
        weightUnit,
        ptPpm: num0(ptPpm),
        pdPpm: num0(pdPpm),
        rhPpm: num0(rhPpm),

        ...(Object.keys(jump).length ? { ppmJumpPct: jump } : {}),

        lmeUnit,
        lme: { pt: num0(ptLme), pd: num0(pdLme), rh: num0(rhLme) },
        fxRate: fxRate === "" ? null : Number(fxRate),
      };

      const d = await api.adminCalcRun({ customerName, input });
      if (!d?.result) throw new Error("No result returned from server");

      setResult(d.result);
      persistLastRates({
        lmeUnit,
        lme: { pt: ptLme, pd: pdLme, rh: rhLme },
        fxRate: fxRate === "" ? null : Number(fxRate),
      });
      setLastEntryId(d.entryId || null);
      showToast("Calculation saved", "ok");
      await loadEntries();
    } catch (e) {
      showToast(e.message || "Something went wrong", "err");
    }
  }

  function loadEntryIntoForm(x) {
    const i = x?.input || {};
    const j = i.ppmJumpPct || null;

    setJumpDetach({
      pt: j?.pt != null,
      pd: j?.pd != null,
      rh: j?.rh != null,
    });

    setCustomerName(x?.customerName || "");
    setWeight(i.weight ?? "");
    setWeightUnit(i.weightUnit || "g");
    setPtPpm(i.ptPpm ?? "");
    setPdPpm(i.pdPpm ?? "");
    setRhPpm(i.rhPpm ?? "");
    setLmeUnit(i.lmeUnit || "per_oz");
    setPtLme(i.lme?.pt ?? "");
    setPdLme(i.lme?.pd ?? "");
    setRhLme(i.lme?.rh ?? "");
    setFxRate(i.fxRate == null ? "" : String(i.fxRate));
    setResult(x?.result || null);
    showToast("Loaded entry into calculator", "ok");
    setPtJumpPct(j?.pt == null ? "" : String(j.pt));
    setPdJumpPct(j?.pd == null ? "" : String(j.pd));
    setRhJumpPct(j?.rh == null ? "" : String(j.rh));
    setPpmDetach({ pt: false, pd: false, rh: false });
    window.scrollTo({ top: 0, behavior: "smooth" });

  }

  if (!authed) return <div className="card">Please login.</div>;
  if (role !== "admin") return <div className="card">Admin only.</div>;

  const r = result;

  const grossKg = (() => {
  const w = Number(weight) || 0;
  if (w <= 0) return 0;
  return weightUnit === "kg" ? w : w / 1000;
})();

  const netKg = r?.netWeightG ? r.netWeightG / 1000 : 0;
  const usdPerKg = grossKg > 0 ? (Number(r?.netUsd || 0) / grossKg) : 0;
  const hasLocal = !!r?.fxRate && r?.netLocal != null;
  const netLocalPerKg = hasLocal && grossKg > 0 ? (Number(r.netLocal) / grossKg) : 0;

  const chargesByMetal = r?.chargesBreakdownUsd?.totalByMetal || null;
  
function enc(s) {
  return encodeURIComponent(s || "");
}

function whatsappShareUrl(text) {
  return `https://api.whatsapp.com/send?text=${enc(text)}`;
}

function buildShareText() {
  if (!r) return "";

  const finalPtPpm = Math.round(ptPpmEff);
  const finalPdPpm = Math.round(pdPpmEff);
  const finalRhPpm = Math.round(rhPpmEff);

  const usdTotal = Number(r.netUsd ?? 0);
  const usdPerKg = netKg ? usdTotal / netKg : 0;

  const lines = [];
  lines.push("Techemet Calculator");

  lines.push(`Customer: ${(customerName || "").trim() || "—"}`);
  lines.push(`Weight: ${fmtNum(netKg, 3)} kg`);

  lines.push(`USD: $${fmtNum(usdTotal, 2)} total • $${fmtNum(usdPerKg, 2)}/kg`);

  if (hasLocal) {
    const localTotal = Number(r.netLocal ?? 0);
    const localPerKg = hasLocal && grossKg > 0 ? (Number(r.netLocal) / grossKg) : 0;
    lines.push(
      `Local: ${fmtNum(localTotal, 2)} total • ${fmtNum(localPerKg, 2)}/kg`
    );
  }

  lines.push(
    `PPM (final): Pt ${finalPtPpm} • Pd ${finalPdPpm} • Rh ${finalRhPpm}`
  );

  const unitLabel = lmeUnit === "per_g" ? "per g" : "per Toz";
  lines.push(
    `LME (${unitLabel}): Pt ${fmtNum(num0(ptLme), 2)} • Pd ${fmtNum(
      num0(pdLme),
      2
    )} • Rh ${fmtNum(num0(rhLme), 2)}`
  );

  return lines.join("\n");
}

function onShareWhatsAppDesktop() {
  if (!r) return;
  const text = buildShareText();
  window.location.assign(whatsappShareUrl(text));
}

async function onShareMobile() {
  if (!r) return;

  const text = buildShareText();
  const title = "Techemet Calculator";

  try {
    if (navigator.share) {
      await navigator.share({ title, text });
      showToast("Shared", "ok");
      return;
    }

    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      showToast("Copied", "ok");
      return;
    }

    window.prompt("Copy this:", text);
  } catch (e) {
    showToast(e?.message || "Share failed", "err");
  }
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function isMobileDevice() {
  if (typeof navigator === "undefined") return false;

  const ua = navigator.userAgent || "";
  const isTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;

  return /Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(ua) || isTouch;
}

const isMobile = isMobileDevice();

  return (
    <div className="gridCalc">
      {/* Inputs */}
      <div className="card">
        <div className="cardHeader">
          <h2>Techemet Calculator</h2>

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>

            <button
              type="button"
              className="iconBtn"
              onClick={() => setShowCalcUnitModal(true)}
              aria-label="Calculator units"
              title="Calculator units"
            >
              <FontAwesomeIcon icon={faGear} />
            </button>
          </div>
        </div>

        <div className="form">
          <label>Customer name</label>
          <input
            ref={refCustomer}
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            onKeyDown={(e) => onAdvanceKeyDown(e, refPtLme)}
            placeholder="e.g. John Doe"
          />


          <label>
            LME prices{" "}
            <span className="smallNote">
              ({LME_UNIT_LABEL[lmeUnit]})
            </span>
          </label>
          <div className="row3">
            <div className="fieldStack">
              <div className="inputWithSuffix">
              <input
                ref={refPtLme}
                type="number"
                value={ptLme}
                onChange={(e) => setPtLme(e.target.value)}
                onKeyDown={(e) => onAdvanceKeyDown(e, refPdLme)}
                placeholder="Platinum"
              />

                <span className="suffix">pt</span>
              </div>
            </div>

            <div className="fieldStack">
              <div className="inputWithSuffix">
              <input
                ref={refPdLme}
                type="number"
                value={pdLme}
                onChange={(e) => setPdLme(e.target.value)}
                onKeyDown={(e) => onAdvanceKeyDown(e, refRhLme)}
                placeholder="Palladium"
              />

                <span className="suffix">pd</span>
              </div>
            </div>

            <div className="fieldStack">
              <div className="inputWithSuffix">
              <input
                ref={refRhLme}
                type="number"
                value={rhLme}
                onChange={(e) => setRhLme(e.target.value)}
                onKeyDown={(e) => onAdvanceKeyDown(e, refWeight)}
                placeholder="Rhodium"
              />

                <span className="suffix">rh</span>
              </div>
            </div>
          </div>

              <label>
                  Weight{" "}
                  <span className="smallNote">
                    ({WEIGHT_UNIT_LABEL[weightUnit]})
                  </span>
                </label>
                  <div className="inputWithSuffix">
                    <input
                      ref={refWeight}
                      type="number"
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                      onKeyDown={(e) => onAdvanceKeyDown(e, refPtPpm)}
                      placeholder="Weight"
                    />

                    <span className="suffix">{weightUnit}</span>
              </div>

          <label>PPM (entered)</label>
          <div className="row3">
            <div className="fieldStack">
              <div className="inputWithSuffix">
              <input
                ref={refPtPpm}
                type="number"
                value={ppmDisplay("pt")}
                onFocus={() => detachPpm("pt")}
                onBlur={() => reattachPpm("pt")}
                onChange={(e) => {
                  detachPpm("pt");
                  setPtPpm(e.target.value);
                }}
                onKeyDown={(e) => onAdvanceKeyDown(e, refPdPpm)}
                placeholder="Pt"
              />

                <span className="suffix">pt</span>
              </div>
            </div>

            <div className="fieldStack">
              <div className="inputWithSuffix">
              <input
                ref={refPdPpm}
                type="number"
                value={ppmDisplay("pd")}
                onFocus={() => detachPpm("pd")}
                onBlur={() => reattachPpm("pd")}
                onChange={(e) => {
                  detachPpm("pd");
                  setPdPpm(e.target.value);
                }}
                onKeyDown={(e) => onAdvanceKeyDown(e, refRhPpm)}
                placeholder="Pd"
              />

                <span className="suffix">pd</span>
              </div>
            </div>

            <div className="fieldStack">
              <div className="inputWithSuffix">
              <input
                ref={refRhPpm}
                type="number"
                value={ppmDisplay("rh")}
                onFocus={() => detachPpm("rh")}
                onBlur={() => reattachPpm("rh")}
                onChange={(e) => {
                  detachPpm("rh");
                  setRhPpm(e.target.value);
                }}
                onKeyDown={onEnterOpenPpmJump}
                placeholder="Rh"
              />

                <span className="suffix">rh</span>
              </div>
            </div>
          </div>

          <div className="ppmJumpToggleRow">
              <button
                ref={refJumpToggle}
                type="button"
                className="ppmJumpToggle"
                onClick={() => setShowPpmJump((v) => !v)}
                onKeyDown={(e) => onToggleEnter(e, showPpmJump, setShowPpmJump, refPtJump)}
                aria-expanded={showPpmJump}
                aria-label="Toggle PPM jump inputs"
                title="PPM Increase %"
              >
                {showPpmJump ? "advanced −" : "advanced +"}
              </button>
          </div>

            {showPpmJump ? (
              <div className="ppmJumpBox">
                <label className="ppmJumpLabel">PPM Increase %</label>

                <div className="row3">
                  {/* Pt */}
                  <div className="inputWithAffix">
                        <input
                          ref={refPtJump}
                          type="number"
                          value={jumpDisplay("pt")}
                          onFocus={() => detachJump("pt")}
                          onChange={(e) => {
                            detachJump("pt");
                            setPtJumpPct(e.target.value);
                          }}
                          onKeyDown={(e) => onAdvanceKeyDown(e, refPdJump)}
                          className={jumpDetach.pt ? "isDetached" : ""}
                        />

                    <button
                      type="button"
                      className="affixBtn"
                      onClick={() => reattachJump("pt")}
                      title="Use saved"
                      tabIndex={-1}
                    >
                      ↺
                    </button>
                    <span className="affixSuffix">pt</span>
                  </div>

                  {/* Pd */}
                  <div className="inputWithAffix">
                      <input
                        ref={refPdJump}
                        type="number"
                        value={jumpDisplay("pd")}
                        onFocus={() => detachJump("pd")}
                        onChange={(e) => {
                          detachJump("pd");
                          setPdJumpPct(e.target.value);
                        }}
                        onKeyDown={(e) => onAdvanceKeyDown(e, refRhJump)}
                        className={jumpDetach.pd ? "isDetached" : ""}
                      />

                    <button
                      type="button"
                      className="affixBtn"
                      onClick={() => reattachJump("pd")}
                      title="Use saved"
                      tabIndex={-1}
                    >
                      ↺
                    </button>
                    <span className="affixSuffix">pd</span>
                  </div>

                  {/* Rh */}
                  <div className="inputWithAffix">
                      <input
                        ref={refRhJump}
                        type="number"
                        value={jumpDisplay("rh")}
                        onFocus={() => detachJump("rh")}
                        onChange={(e) => {
                          detachJump("rh");
                          setRhJumpPct(e.target.value);
                        }}
                        onKeyDown={(e) => onAdvanceKeyDown(e, refFx)}
                        className={jumpDetach.rh ? "isDetached" : ""}
                      />

                    <button
                      type="button"
                      className="affixBtn"
                      onClick={() => reattachJump("rh")}
                      title="Use saved"
                      tabIndex={-1}
                    >
                      ↺
                    </button>
                    <span className="affixSuffix">rh</span>
                  </div>
                </div>
              </div>
            ) : null}


          <label>USD exchange rate (optional)</label>
          <input
            ref={refFx}
            type="number"
            value={fxRate}
            onChange={(e) => setFxRate(e.target.value)}
            onKeyDown={onEnterRun}
            placeholder="If set, shows local price too"
          />


          <button ref={refCalcBtn} type="button" className="btnPrimary" onClick={run}>
            Calculate & save
          </button>


          {msg ? <div className="msg">{msg}</div> : null}
        </div>
      </div>

      {/* Results + Entries */}
      <div className="grid" style={{ gap: 14 }}>
        <div className="card">
        <div className="cardHeader">
        <h2>Result</h2>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <div className="smallNote">{r ? "Calculated" : "—"}</div>

             {isMobile ? (
              <button
                type="button"
                className="iconBtn"
                onClick={onShareMobile}
                disabled={!r}
                aria-label="Share"
                title={r ? "Share" : "Calculate first"}
              >
                <FontAwesomeIcon icon={faShareNodes} />
              </button>
            ) : (
              <button
                type="button"
                className="iconBtn"
                onClick={onShareWhatsAppDesktop}
                disabled={!r}
                aria-label="Share on WhatsApp"
                title={r ? "Share on WhatsApp" : "Calculate first"}
              >
                {/* Option A: icon */}
                <FontAwesomeIcon icon={faWhatsapp} />
                {/* Option B (if you didn't install brands): WhatsApp */}
              </button>
            )}

            <button
              type="button"
              onClick={async () => {
                try {
                  const safeName = (customerName || "customer").toString().replace(/[^\w\-]+/g, "_").slice(0, 40);
                  const { blob, contentDisposition } = await api.adminCalcDownloadPdf(lastEntryId);

                  let filename = `settlement_${safeName}_${String(lastEntryId).slice(-6)}.pdf`;
                  const m = /filename="([^"]+)"/i.exec(contentDisposition || "");
                  if (m?.[1]) filename = m[1];

                  downloadBlob(blob, filename);
                  showToast("PDF downloaded", "ok");
                } catch (e) {
                  showToast(e.message || "Failed to download PDF", "err");
                }
              }}
              disabled={!lastEntryId}
            >
              Download PDF
            </button>
        </div>
        </div>

          {r ? (
            <div className="appear">
              <div className="result4Head">
                  <div className="result4HeadCell">USD</div>
                  <div className="result4HeadCell result4HeadRight">Local</div>
                </div>
              <div className="result4Grid">
                {/* Row 1 */}
                <div className="resultCell">
                  <div className="resultValue">
                    <SlotNumber value={r.netUsd} decimals={2} prefix="$" suffix="/total" />
                  </div>
                </div>

                <div className="resultCell resultCellRight">
                  {hasLocal ? (
                    <div className="resultValue">
                      <SlotNumber value={Number(r.netLocal)} decimals={2} suffix="/total" />
                    </div>
                  ) : (
                    <div className="resultValue resultMuted">—</div>
                  )}
                </div>

                {/* Row 2 */}
                <div className="resultCell">
                  <div className="resultValue">
                    <SlotNumber value={usdPerKg} decimals={2} prefix="$" suffix="/kg" />
                  </div>
                </div>

                <div className="resultCell resultCellRight">
                  {hasLocal ? (
                    <div className="resultValue">
                      <SlotNumber value={netLocalPerKg} decimals={2} suffix="/kg" />
                    </div>
                  ) : (
                    <div className="resultValue resultMuted">—</div>
                  )}
                </div>
              </div>


              {/* ppm jump display */}
              <div className="sep" />
              <div className="smallNote">
                PPM after jump: Pt <b>{fmtNum(r.ppm?.final?.pt, 0)}</b> (jump {fmtNum(r.ppm?.jumpPct?.pt, 2)}%) •
                Pd <b>{fmtNum(r.ppm?.final?.pd, 0)}</b> (jump {fmtNum(r.ppm?.jumpPct?.pd, 2)}%) •
                Rh <b>{fmtNum(r.ppm?.final?.rh, 0)}</b> (jump {fmtNum(r.ppm?.jumpPct?.rh, 2)}%)
              </div>

              {/* charges breakdown */}
              <div className="sep" />
              <div className="sectionTitle">Charges by metal (USD)</div>
              {chargesByMetal ? (
                <div className="tableWrap">
                  <table className="table" style={{ minWidth: 520 }}>
                    <thead>
                      <tr>
                        <th>Metal</th>
                        <th>Charges</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Pt</td>
                        <td style={{ fontWeight: 800 }}>{fmtUsd(chargesByMetal.pt)}</td>
                      </tr>
                      <tr>
                        <td>Pd</td>
                        <td style={{ fontWeight: 800 }}>{fmtUsd(chargesByMetal.pd)}</td>
                      </tr>
                      <tr>
                        <td>Rh</td>
                        <td style={{ fontWeight: 800 }}>{fmtUsd(chargesByMetal.rh)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="smallNote">No charge breakdown returned.</div>
              )}

              <div className="sep" />

              <div className="tableWrap">
                <table className="table" style={{ minWidth: 520 }}>
                  <thead>
                    <tr>
                      <th>Metal</th>
                      <th>Recovered (g)</th>
                      <th>Recovered (oz)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Platinum</td>
                      <td>{fmtNum(r.recoveredG?.pt, 4)}</td>
                      <td>{fmtNum(r.recoveredOz?.pt, 6)}</td>
                    </tr>
                    <tr>
                      <td>Palladium</td>
                      <td>{fmtNum(r.recoveredG?.pd, 4)}</td>
                      <td>{fmtNum(r.recoveredOz?.pd, 6)}</td>
                    </tr>
                    <tr>
                      <td>Rhodium</td>
                      <td>{fmtNum(r.recoveredG?.rh, 4)}</td>
                      <td>{fmtNum(r.recoveredOz?.rh, 6)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="smallNote">Run a calculation to see results.</div>
          )}
        </div>

        {toast ? (
          <div className={`toast ${toast.type === "err" ? "toastErr" : "toastOk"}`}>
            {toast.text}
          </div>
        ) : null}
      </div>


      <div className="card gridFullWidth">
        {/* Entries */}
        <div className="card">
          <div className="cardHeader">
            <h2>Saved entries</h2>
            <div className="smallNote">{loadingEntries ? "Loading…" : `${entries.length} shown`}</div>
          </div>

          <div className="adminToolbar" style={{ marginBottom: 10 }}>
            <input
              placeholder="Search customer…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="desc">Newest</option>
              <option value="asc">Oldest</option>
            </select>
            <select value={cap} onChange={(e) => setCap(Number(e.target.value))}>
              <option value={10}>Last 10</option>
              <option value={50}>Last 50</option>
              <option value={100}>Last 100</option>
            </select>

            <button type="button" onClick={() => loadEntries(true)}>Refresh</button>
          </div>

            {/* Desktop table */}
            <div className="tableWrap desktopOnly">
              <table className="table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Customer</th>
                    <th>Weight</th>
                    <th>Net USD</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((x) => (
                    <tr key={x._id}>
                      <td>{fmtDate(x.createdAt)}</td>
                      <td style={{ fontWeight: 800 }}>{x.customerName || "—"}</td>
                      <td>{fmtNum(x.input?.weight, 0)} {x.input?.weightUnit}</td>
                      <td style={{ fontWeight: 800 }}>{fmtUsd(x.result?.netUsd)}</td>
                      <td style={{ textAlign: "right" }}>
                        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const safeName = (x.customerName || "customer").toString().replace(/[^\w\-]+/g, "_").slice(0, 40);
                                const { blob, contentDisposition } = await api.adminCalcDownloadPdf(x._id);
                                let filename = `settlement_${safeName}_${String(x._id).slice(-6)}.pdf`;
                                const m = /filename="([^"]+)"/i.exec(contentDisposition || "");
                                if (m?.[1]) filename = m[1];
                                downloadBlob(blob, filename);
                                showToast("PDF downloaded", "ok");
                              } catch (e) {
                                showToast(e.message || "Failed to download PDF", "err");
                              }
                            }}
                          >
                            Download PDF
                          </button>

                          <button type="button" className="btnPrimary" onClick={() => loadEntryIntoForm(x)}>
                            Load
                          </button>

                          <button type="button" onClick={() => setConfirmDelete(x)} disabled={deletingId === x._id}>
                            {deletingId === x._id ? "Deleting…" : "Delete"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!entries.length ? (
                    <tr><td colSpan={5} className="smallNote">No entries (or expired after 72h).</td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="mobileOnly">
              {!entries.length ? (
                <div className="smallNote">No entries (or expired after 72h).</div>
              ) : (
                entries.map((x) => (
                  <div className="mRow" key={x._id}>
                    <div className="mRowTop">
                      <div>
                        <div className="mRowTitle">{x.customerName || "—"}</div>
                        <div className="mRowMeta">{fmtDate(x.createdAt)}</div>
                      </div>
                      <div className="mStrong">{fmtUsd(x.result?.netUsd)}</div>
                    </div>

                    <div className="mGrid">
                      <div className="mField">
                        <div className="mLabel">Weight</div>
                        <div className="mValue">{fmtNum(x.input?.weight, 0)} {x.input?.weightUnit}</div>
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const safeName = (x.customerName || "customer").toString().replace(/[^\w\-]+/g, "_").slice(0, 40);
                            const { blob, contentDisposition } = await api.adminCalcDownloadPdf(x._id);
                            let filename = `settlement_${safeName}_${String(x._id).slice(-6)}.pdf`;
                            const m = /filename="([^"]+)"/i.exec(contentDisposition || "");
                            if (m?.[1]) filename = m[1];
                            downloadBlob(blob, filename);
                            showToast("PDF downloaded", "ok");
                          } catch (e) {
                            showToast(e.message || "Failed to download PDF", "err");
                          }
                        }}
                      >
                        Download PDF
                      </button>

                      <button type="button" className="btnPrimary" onClick={() => loadEntryIntoForm(x)}>
                        Load
                      </button>

                      <button type="button" onClick={() => setConfirmDelete(x)} disabled={deletingId === x._id}>
                        {deletingId === x._id ? "Deleting…" : "Delete"}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
        </div>



      </div>
        <div className="card gridFullWidth">
        {/* Settings */}
        <div className="card">
        <div className="cardHeader">
        <h2>{showSettings ? "My admin calc settings" : "CRG"}</h2>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {showSettings ? (
            <div className="smallNote">Used only for your admin calculator</div>
            ) : null}

            <button type="button" onClick={() => setShowSettings((v) => !v)}>
            {showSettings ? "Hide" : "Show"}
            </button>
        </div>
        </div>

            {!showSettings ? (
             <div className="smallNote">Settings are hidden.</div>
               ) : settings ? (
               <>
                <div className="fieldRow">
                  <div className="fieldItem">
                    <label>Moisture %</label>
                    <input
                      type="number"
                      value={settings.moisturePct ?? ""}
                      onChange={(e) =>
                        setSettings({ ...settings, moisturePct: e.target.value })
                      }
                    />
                  </div>

                  <div className="fieldItem">
                    <label>My margin %</label>
                    <input
                      type="number"
                      value={settings.marginPct ?? ""}
                      onChange={(e) =>
                        setSettings({ ...settings, marginPct: e.target.value })
                      }
                    />
                  </div>
                </div>


              <div className="sep" />

              <div className="sectionTitle">Lease days</div>
              <div className="row3 row3Fill">
                {["pt", "pd", "rh"].map((k) => (
                  <div key={k}>
                    <label>{k.toUpperCase()}</label>
                    <input
                      type="number"
                      value={settings.leaseDays?.[k] ?? ""}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          leaseDays: { ...(settings.leaseDays || {}), [k]: e.target.value },
                        })
                      }
                    />
                  </div>
                ))}
              </div>

              <div className="sep" />

              <div className="sectionTitle">Lease Rates</div>
              <div className="row3 row3Fill">
                {["pt", "pd", "rh"].map((k) => (
                  <div key={k}>
                    <label>{k.toUpperCase()}</label>
                    <input
                      type="number"
                      value={settings.chargeUsdPerG?.[k] ?? ""}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          chargeUsdPerG: { ...(settings.chargeUsdPerG || {}), [k]: e.target.value },
                        })
                      }
                    />
                  </div>
                ))}
              </div>

              <div className="sep" />

              <div className="sectionTitle">Recovery rate (%)</div>
              <div className="row3 row3Fill">
                {["pt", "pd", "rh"].map((k) => {
                  const dec = settings?.recoveryRate?.[k];
                  const pctDisplay =
                    dec === "" || dec == null ? "" : String(Math.round(Number(dec) * 100));

                  return (
                    <div key={k}>
                      <label>{k.toUpperCase()}</label>
                      <div className="inputWithSuffix">
                      <input 
                        type="number" 
                        step="1"
                        min="0"
                        max="100"
                        value={pctDisplay}
                        onChange={(e) => {
                          const raw = e.target.value; // user types percent
                          // allow empty while typing
                          const nextDec =
                            raw === "" ? "" : Math.max(0, Math.min(100, Number(raw))) / 100;

                          setSettings({
                            ...settings,
                            recoveryRate: {
                              ...(settings.recoveryRate || {}),
                              [k]: nextDec,
                            },
                          });
                        }}
                      />
                      <span className="suffix">%</span>
                    </div>
                    </div>
                  );
                })}
              </div>

              <div className="sep" />

              <div className="sectionTitle">PPM Increase %</div>
              <div className="row3 row3Fill">
                {["pt", "pd", "rh"].map((k) => (
                  <div key={k}>
                    <label>{k.toUpperCase()}</label>
                    <input
                      type="number"
                      value={settings.ppmJumpPct?.[k] ?? ""}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          ppmJumpPct: { ...(settings.ppmJumpPct || {}), [k]: e.target.value },
                        })
                      }
                    />
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 10, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <button type="button" className="btnPrimary" onClick={saveSettings}>
                  Save my settings
                </button>
              </div>
            </>
          ) : (
            <div className="smallNote">Loading settings…</div>
          )}
        </div>
            </div>

      {confirmDelete ? (
  <div className="modalBackdrop">
    <div className="modalCard">
      <h3 style={{ marginTop: 0 }}>Delete entry?</h3>

      <div className="smallNote" style={{ marginBottom: 12 }}>
        This will permanently delete the entry for{" "}
        <b>{confirmDelete.customerName || "—"}</b> from{" "}
        <b>{fmtDate(confirmDelete.createdAt)}</b>.
      </div>

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button type="button" onClick={() => setConfirmDelete(null)} disabled={deletingId === confirmDelete._id}>
          Cancel
        </button>

        <button
          type="button"
          className="btnPrimary"
          onClick={() => deleteEntry(confirmDelete)}
          disabled={deletingId === confirmDelete._id}
        >
          Yes, delete
        </button>
      </div>
    </div>
  </div>
) : null}

{showCalcUnitModal ? (
  <div className="modalBackdrop" onMouseDown={() => setShowCalcUnitModal(false)}>
    <div className="modalCard" onMouseDown={(e) => e.stopPropagation()}>
      <div className="cardHeader" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Calculator units</h2>
            <button
              type="button"
              className="iconBtn btnGlass"
              onClick={() => setShowCalcUnitModal(false)}
              aria-label="Close"
            >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>

      <div className="smallNote" style={{ marginBottom: 12 }}>
        These affect how inputs are interpreted.
      </div>

      <div style={{ display: "grid", gap: 12 }}>
        <div>
          <label style={{ display: "block", marginBottom: 6 }}>LME input unit</label>
            <div className="customSelect">
                  <button
                    ref={refLmeTrigger}
                    type="button"
                    className="customSelectTrigger"
                    onClick={() => setLmeOpen((v) => !v)}
                    onKeyDown={(e) => onSelectTriggerEnter(e, lmeOpen, setLmeOpen, refLmeOptOz)}
                    aria-haspopup="listbox"
                    aria-expanded={lmeOpen}
                  >
                {lmeUnit === "per_oz" ? "per ounce" : "per gram"}
                <span className="chev">▾</span>
              </button>

              {lmeOpen && (
                <div className="customSelectMenu">
                    <div
                      ref={refLmeOptOz}
                      tabIndex={0}
                      role="option"
                      aria-selected={lmeUnit === "per_oz"}
                      className={`customOption ${lmeUnit === "per_oz" ? "active" : ""}`}
                      onClick={() => {
                        setLmeUnit("per_oz");
                        setLmeOpen(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === "NumpadEnter") {
                          e.preventDefault();
                          setLmeUnit("per_oz");
                          setLmeOpen(false);
                          focusNext(refWeightTrigger); // continue flow
                        }
                      }}
                    >
                      per ounce
                    </div>


                  <div
                    className={`customOption ${lmeUnit === "per_g" ? "active" : ""}`}
                    onClick={() => {
                      setLmeUnit("per_g");
                      setLmeOpen(false);
                    }}
                  >
                    per gram
                  </div>
                </div>
              )}
            </div>
        </div>

        <div>
          <label style={{ display: "block", marginBottom: 6 }}>Weight unit</label>
            <div className="customSelect">
                  <button
                    ref={refWeightTrigger}
                    type="button"
                    className="customSelectTrigger"
                    onClick={() => setWeightOpen((v) => !v)}
                    onKeyDown={(e) =>
                      onSelectTriggerEnter(e, weightOpen, setWeightOpen, refWeightOptG)
                    }
                    aria-haspopup="listbox"
                    aria-expanded={weightOpen}
                  >
                {weightUnit === "g" ? "grams" : "kg"}
                <span className="chev">▾</span>
              </button>

              {weightOpen && (
                <div className="customSelectMenu" role="listbox" aria-label="Weight unit">
                  <div
                    ref={refWeightOptG}
                    tabIndex={0}
                    role="option"
                    aria-selected={weightUnit === "g"}
                    className={`customOption ${weightUnit === "g" ? "active" : ""}`}
                    onClick={() => {
                      setWeightUnit("g");
                      setWeightOpen(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === "NumpadEnter") {
                        e.preventDefault();
                        setWeightUnit("g");
                        setWeightOpen(false);
                        // next: go to Done button (create a ref if you want)
                      }
                    }}
                  >
                    grams
                  </div>


                  <div
                    role="option"
                    aria-selected={weightUnit === "kg"}
                    className={`customOption ${weightUnit === "kg" ? "active" : ""}`}
                    onClick={() => {
                      setWeightUnit("kg");
                      setWeightOpen(false);
                    }}
                  >
                    kg
                  </div>
                </div>
              )}
            </div>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 14 }}>
        <button
          type="button"
          className="btnGlass"
          onClick={() => setShowCalcUnitModal(false)}
        >
          Done
        </button>
      </div>
    </div>
  </div>
) : null}

    </div>
  );
}